import React, { useState } from 'react';
import { AlertTriangle, ArrowLeft, FileDown, Loader2, Plus, X } from 'lucide-react';
import { Report, UserProfile } from '../../types';
import { SifaTaetigkeit } from '../../services/sifa/betreuungTypes';
import { deleteTaetigkeit } from '../../services/sifa/sifaBetreuungService';
import { formatDauer, sollIstVergleich, summeMinuten } from '../../services/sifa/taetigkeitenLogic';
import { erzeugeTaetigkeitsnachweisPdf } from '../../services/sifa/taetigkeitsnachweisPdf';
import { CompanyBase } from '../../services/pdf/companyForPdf';
import { describeError } from '../../services/errorMessage';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useTaetigkeiten } from './useTaetigkeiten';
import { TaetigkeitenListe } from './TaetigkeitenListe';
import { TaetigkeitDialog } from './TaetigkeitDialog';
import { formatDatum } from './unterweisungModulLogic';
import { balkenProzent, jahresListe, nachweisDateiname, nutzerName, verteilungJeArt } from './taetigkeitenModulLogic';

interface Props {
  project: { id: string; name?: string; data: Report };
  userProfile: UserProfile | null;
  /** Firmenstammdaten fuer den Kopf des PDF (App.invoiceCompany). */
  company?: CompanyBase;
  onBack: () => void;
  isReadOnly?: boolean;
}

/**
 * Taetigkeitsnachweis eines SiFa-Projekts — duenner Container.
 *
 * Jahresauswahl, Soll/Ist gegen die Einsatzzeit aus den Betriebsdaten
 * (metadata.sifa.einsatzzeitSollStunden), Verteilung je Art, Liste,
 * Erfassen/Bearbeiten/Loeschen (nicht bei gebuchtAm) und das PDF
 * „Tätigkeitsnachweis <Jahr>". Kein onUpdate: dieses Modul schreibt nie ins
 * Projekt-JSON — die Abrechnung (Stufe 3) bucht ausdruecklich, nie still.
 */
export const TaetigkeitenModul: React.FC<Props> = ({ project, userProfile, company, onBack, isReadOnly = false }) => {
  const isMobile = useIsMobile();
  const meta = project.data?.metadata;
  const sollStunden = meta?.sifa?.einsatzzeitSollStunden;
  const aktuellesJahr = new Date().getFullYear();
  const [jahr, setJahr] = useState(aktuellesJahr);
  const { taetigkeiten, laden, fehler, uebernehmen, entfernen } = useTaetigkeiten(project.id, jahr);

  const [dialog, setDialog] = useState<{ offen: boolean; t: SifaTaetigkeit | null }>({ offen: false, t: null });
  const [pdfLaeuft, setPdfLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  const name = nutzerName(userProfile);
  const minuten = summeMinuten(taetigkeiten);
  const sollIst = sollIstVergleich(minuten, sollStunden ?? null);
  const prozent = balkenProzent(minuten, sollStunden);
  const verteilung = verteilungJeArt(taetigkeiten);

  const loeschen = async (t: SifaTaetigkeit) => {
    if (isReadOnly || t.gebuchtAm) return;
    const ok = window.confirm(`Tätigkeit „${t.titel}" vom ${formatDatum(t.datum)} (${formatDauer(t.dauerMinuten)}) löschen?\n\nDas lässt sich nicht rückgängig machen.`);
    if (!ok) return;
    try {
      await deleteTaetigkeit(t.id);
      entfernen(t.id);
      zeigeInfo(`„${t.titel}" gelöscht.`);
    } catch (e) {
      zeigeFehler(describeError(e));
    }
  };

  /**
   * PDF als Blob vom reinen Generator, dann ueber einen Anker herunterladen.
   * Die URL wird erst nach 10 s freigegeben: Safari/iOS starten den Download
   * erst nach dem Klick-Handler (Muster ErgebnisAnsicht).
   */
  const pdf = async () => {
    if (pdfLaeuft) return;
    setPdfLaeuft(true);
    setMeldung(null);
    try {
      const blob = erzeugeTaetigkeitsnachweisPdf({
        projektName: project.name || meta?.projectName || '',
        projektNummer: meta?.projectNumber || '',
        betrieb: meta?.customerName || meta?.clientName || project.name || meta?.projectName || '',
        jahr,
        taetigkeiten,
        sollStunden: sollStunden ?? null,
        sifaName: name || '',
        company: company ? { name: company.name, adresse: company.address, logoDataUrl: company.logo } : null,
      });
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = nachweisDateiname(jahr, meta?.projectNumber);
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
    } catch (e) {
      zeigeFehler(describeError(e));
    } finally {
      setPdfLaeuft(false);
    }
  };

  return (
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">Tätigkeitsnachweis</h1>
          <p className="text-xs text-slate-500 truncate">{project.name || meta?.projectName} · {taetigkeiten.length} Einträge {jahr}</p>
        </div>
        <div className="flex gap-2">
          <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))} className="min-h-[44px] px-3 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl outline-none">
            {jahresListe(aktuellesJahr).map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
          {!isMobile && (
            <button onClick={() => void pdf()} disabled={pdfLaeuft || laden} className="min-h-[44px] px-3 flex items-center gap-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl disabled:opacity-40">
              {pdfLaeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF Tätigkeitsnachweis {jahr}
            </button>
          )}
          {!isReadOnly && (
            <button onClick={() => setDialog({ offen: true, t: null })} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl">
              <Plus className="w-4 h-4" /> Erfassen
            </button>
          )}
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
        {isReadOnly && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Tätigkeiten lassen sich ansehen und als PDF ausgeben — erfassen, ändern und löschen ist gesperrt.
          </div>
        )}
        {(meldung || fehler) && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' || fehler ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {(meldungTon === 'fehler' || fehler) && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1">{meldung || fehler}</span>
            {meldung && <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}

        {/* Kopf: Ist gegen Soll-Einsatzzeit (DGUV V2 Anlage 2) und die Verteilung je Art. */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase text-slate-500">Einsatzzeit {jahr}</p>
            <p className="text-2xl font-black text-slate-800 mt-1">
              {formatDauer(minuten)}
              {sollStunden ? <span className="text-base font-semibold text-slate-400"> / {sollStunden} h Soll</span> : null}
            </p>
            {sollStunden ? (
              <>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
                  <div className={`h-full ${prozent >= 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${prozent}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">{sollIst.prozent} % der vereinbarten Einsatzzeit erbracht{sollIst.restStunden > 0 ? ` · noch ${sollIst.restStunden.toLocaleString('de-DE')} h bis zum Soll` : ''}</p>
              </>
            ) : (
              <p className="text-xs text-slate-500 mt-1">Kein Soll hinterlegt — die Einsatzzeit steht in den Betriebsdaten des Projekts.</p>
            )}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase text-slate-500">Verteilung je Art</p>
            {verteilung.length === 0 ? (
              <p className="text-xs text-slate-400 mt-1">Noch nichts erfasst.</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {verteilung.slice(0, 6).map((v) => (
                  <li key={v.art} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-700 font-semibold truncate">{v.label} <span className="text-slate-400 font-normal">({v.anzahl})</span></span>
                      <span className="text-slate-600 font-mono whitespace-nowrap">{formatDauer(v.minuten)} · {v.prozent} %</span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden mt-0.5"><div className="h-full bg-indigo-400" style={{ width: `${v.prozent}%` }} /></div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <TaetigkeitenListe
          taetigkeiten={taetigkeiten}
          laden={laden}
          jahr={jahr}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          onBearbeiten={(t) => setDialog({ offen: true, t })}
          onLoeschen={(t) => void loeschen(t)}
        />
      </div>

      {dialog.offen && (
        <TaetigkeitDialog
          key={dialog.t?.id || 'neu'}
          projectId={project.id}
          taetigkeit={dialog.t}
          nutzerName={name}
          isReadOnly={isReadOnly}
          onClose={() => setDialog({ offen: false, t: null })}
          onGespeichert={(t) => { uebernehmen(t); setDialog({ offen: false, t: null }); zeigeInfo(`„${t.titel}" gespeichert.`); }}
        />
      )}
    </div>
  );
};
